import { createApp } from 'vue'
import { IonicVue } from '@ionic/vue'

import App from './App.vue'
import router from './router'
import i18n from './i18n'
import { AuthService } from './services/AuthService'
import { GuideService } from './services/GuideService'

// Initialize authentication before router
AuthService.initializeAuth()

// Initialize guide service with default stavanger guide
const initializeApp = async () => {
  try {
    const savedGuideLocation = localStorage.getItem('selected-guide-location') || 'stavanger'
    await GuideService.initialize('en', savedGuideLocation)
    console.log('✅ Guide service initialized with:', savedGuideLocation)
  } catch (error) {
    console.error('❌ Failed to initialize guide service:', error)
  }
}

initializeApp()

import '@ionic/vue/css/core.css'
import '@ionic/vue/css/normalize.css'
import '@ionic/vue/css/structure.css'
import '@ionic/vue/css/typography.css'
import '@ionic/vue/css/ionic.bundle.css'

const app = createApp(App)
  .use(IonicVue, {
    mode: 'ios'
  })
  .use(router)
  .use(i18n)

router.isReady().then(async () => {
  // Verify authentication on app load if user appears to be authenticated
  /*if (AuthService.checkAuth()) {
    console.log('🔍 Verifying authentication on app load...')
    alert('Verifying authentication on app load...')
    try {
      const success = await AuthService.login(AuthService.userEmail.value.trim(), AuthService.accessCode.value.trim())
      console.log('✅ Authentication verified on app load:', success)
      alert('Authentication verified on app load:' + success)
      if (!success) {
        alert('Authentication failed on app load, logging out')
        console.log('❌ Authentication failed on app load, logging out')
        AuthService.logout()
        // Redirect to login if not already there
        if (router.currentRoute.value.path !== '/login') {
          router.push('/login')
        }
      }
    } catch (error: any) {
      console.log('❌ Authentication verification failed on app load:', error)
      // Check if it's a network error (no internet connection)
      if (error.message?.includes('NETWORK_ERROR')) {
        alert('No internet connection, allowing offline access')
        console.log('🌐 No internet connection, allowing offline access')
        // Allow user to proceed when offline
      } else {
        alert('Authentication error, logging out')
        console.log('❌ Authentication error, logging out')
        AuthService.logout()
        // Redirect to login if not already there
        if (router.currentRoute.value.path !== '/login') {
          router.push('/login')
        }
      }
    }
  }*/
  
  app.mount('#app')
  
  // Add global image fullscreen functionality after a short delay
  setTimeout(() => {
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'IMG' && target.closest('.modal-content')) {
        e.preventDefault()
        console.log('Image clicked, attempting fullscreen...')
        
        // Check if it's a small image (header image) or text content image
        const isHeaderImage = target.closest('.modal-header') || target.closest('[class*="header"]')
        
        if (isHeaderImage) {
          // Small header images - fullscreen without black background (like before)
          const requestFullscreen = target.requestFullscreen || 
                                   (target as any).webkitRequestFullscreen || 
                                   (target as any).mozRequestFullScreen || 
                                   (target as any).msRequestFullscreen
          
          if (requestFullscreen) {
            requestFullscreen.call(target).catch((err: any) => {
              console.error('Fullscreen failed:', err)
            })
          }
        } else {
          // Text content images - fullscreen with black background and close button
          const container = createFullscreenContainer(target as HTMLImageElement)
          
          const requestFullscreen = container.requestFullscreen || 
                                   (container as any).webkitRequestFullscreen || 
                                   (container as any).mozRequestFullScreen || 
                                   (container as any).msRequestFullscreen
          
          if (requestFullscreen) {
            requestFullscreen.call(container).then(() => {
              console.log('Fullscreen entered with close button...')
            }).catch((err: any) => {
              console.error('Fullscreen failed:', err)
              // Clean up if fullscreen fails
              container.remove()
            })
          } else {
            console.log('Fullscreen not supported')
            container.remove()
          }
        }
      }
    })
    
    // Listen for fullscreen exit to clean up
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement) {
        const container = document.querySelector('.fullscreen-image-container')
        if (container) {
          container.remove()
        }
      }
    })
  }, 100)
  
  // Global image error handler
  document.addEventListener('error', (event) => {
    const target = event.target as HTMLElement
    if (target.tagName === 'IMG') {
      console.log('🖼️ Image failed to load:', (target as HTMLImageElement).src)
      target.style.display = 'none'
    }
  }, true)

  // Function to create fullscreen container with image and close button
  function createFullscreenContainer(img: HTMLImageElement): HTMLElement {
    const container = document.createElement('div')
    container.className = 'fullscreen-image-container'
    container.style.cssText = `
      position: relative;
      width: 100vw;
      height: 100vh;
      background: black;
      display: flex;
      align-items: center;
      justify-content: center;
    `
    
    // Clone the image
    const clonedImg = img.cloneNode(true) as HTMLImageElement
    clonedImg.style.cssText = `
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    `
    
    // Ensure the image loads properly
    if (img.complete) {
      // Image is already loaded, copy dimensions
      clonedImg.width = img.naturalWidth
      clonedImg.height = img.naturalHeight
    } else {
      // Wait for image to load
      img.onload = () => {
        clonedImg.width = img.naturalWidth
        clonedImg.height = img.naturalHeight
      }
    }
    
    // Create close button
    const closeBtn = document.createElement('button')
    closeBtn.innerHTML = '✕'
    closeBtn.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      z-index: 999999;
      background: rgba(0,0,0,0.8);
      color: white;
      border: 2px solid white;
      border-radius: 50%;
      width: 60px;
      height: 60px;
      font-size: 28px;
      font-weight: bold;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
    `
    
    closeBtn.onclick = (e) => {
      e.preventDefault()
      e.stopPropagation()
      console.log('Close button clicked')
      const exitFullscreen = document.exitFullscreen || 
                            (document as any).webkitExitFullscreen || 
                            (document as any).mozCancelFullScreen || 
                            (document as any).msExitFullscreen
      if (exitFullscreen) {
        exitFullscreen.call(document)
      }
    }
    
    // Add elements to container
    container.appendChild(clonedImg)
    container.appendChild(closeBtn)
    
    // Add container to body
    document.body.appendChild(container)
    
    return container
  }
  

})


// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    console.log('Registering service worker')
    navigator.serviceWorker.register(`${process.env.BASE_URL}sw.js`)
      .then(registration => {
        console.log('SW registered: ', registration)
      })
      .catch(registrationError => {
        console.log('SW registration failed: ', registrationError)
      })
  })
}

// PWA Install prompt
let deferredPrompt: any;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;
  console.log('PWA install prompt ready');
});
